import { useQuery } from '@tanstack/react-query';
import { AgenticInsightsApi } from '@/features/agentic-insights/api/agenticInsightsApi';
import { agenticInsightsQueryKeys } from '@/features/agentic-insights/queryKeys';
import { useAgenticRunDataSource } from './agenticRunDataSource';
import { getRunFixture } from './fixtures';
import { resolveRunPollInterval } from './runLifecycle';
import type { AgenticRun } from './types';
import { useAgenticRun } from './useAgenticRun';

/**
 * The run the page body renders. Defaults to the latest run — reusing `useAgenticRun`'s exact query
 * so the two hooks share one cache entry and one poll instead of hitting `.../runs/latest` twice.
 * Once a specific runId is selected, this becomes an independent query against that id, inert once
 * the run is terminal (`resolveRunPollInterval` keys on status, not on "is this the latest run").
 */
export const useViewedRun = (loopId: number, selectedRunId: string | undefined) => {
  const { dataSource } = useAgenticRunDataSource();
  const liveRun = useAgenticRun(loopId);
  const runId = selectedRunId ?? '';

  const byId = useQuery<AgenticRun | null>({
    queryKey: agenticInsightsQueryKeys.viewedRun.byId(dataSource, loopId, runId),
    queryFn: () =>
      dataSource === 'mock'
        ? Promise.resolve(getRunFixture(loopId, runId))
        : AgenticInsightsApi.getRun(loopId, runId),
    refetchInterval: (query) => resolveRunPollInterval(query.state.data, Date.now()),
    enabled: selectedRunId !== undefined,
    // See useAgenticRun.ts: dataSource is part of the query key, so without this a pinned run would
    // flash to the loading skeleton for an instant every time Sample data is toggled.
    placeholderData: (previousData) => previousData,
  });

  return selectedRunId === undefined ? liveRun : byId;
};
